import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { AllocationData } from "../../../../cubiq-model-multi-asset/model/AllocationData";
import { ITraderGroupLookupService } from "../../../lookup/model/ITraderGroupLookupService";
import { OrderData } from "shared/cubiq-model-multi-asset/model/OrderData";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ValueGetterParams } from "ag-grid-community";

export class BmarkAllocationHelperModel {
  public allocationId: CubiqTS.Long; // primary
  public orderId: string; // reference
  public allocationType: CubiqTS.AllocationType;
  public baseCcy: string;
  public nonBaseCcy: string;
  public traderGroupName: string;
  public traderGroupId: CubiqTS.Long;

  constructor(allocationId: CubiqTS.Long) {
    this.allocationId = allocationId;
  }
}

export class BmarkTraderGroupNameHelper {
  constructor(
    protected traderGroupLookup: ITraderGroupLookupService,
    protected traderGroupColumn: string,
  ) {}

  /* NEW APPROACH */
  public getTraderGroupNames(params: ValueGetterParams): string {
    let result: string = "";
    let orderData: CubiqTS.OrderDataImpl = params != null ? params.data : null;

    if (
      orderData instanceof CubiqTS.OrderDataImpl &&
      orderData.orderType === CubiqTS.OrderType.BENCHMARK
    ) {
      // All top-level allocations will have same set of trader groups
      if (
        Array.isArray(orderData.allocationData) &&
        orderData.allocationData.length >= 1 &&
        orderData.allocationData[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        let allocationData: CubiqTS.AllocationDataImpl = orderData.allocationData[0];
        let allocationMetaMap: Map<string, BmarkAllocationHelperModel> = new Map();
        this.processAllocation(allocationData, allocationMetaMap);
        if (
          allocationData.allocationType === CubiqTS.AllocationType.PRIME ||
          allocationMetaMap.size === 1
        ) {
          // CROSS || PRIME
          result =
            allocationMetaMap.get(allocationData.allocationId?.toString()) instanceof
            BmarkAllocationHelperModel
              ? allocationMetaMap.get(allocationData.allocationId?.toString()).traderGroupName
              : "-";
        } else if (allocationMetaMap.size > 1) {
          // component allocations
          let baseCcy = orderData.isInvertedOrderFg
            ? allocationData.nonbaseCcy
            : allocationData.baseCcy;
          for (let [key, helperModel] of allocationMetaMap.entries()) {
            if (
              key != allocationData.allocationId?.toString() &&
              result != helperModel.traderGroupName
            ) {
              if (helperModel.baseCcy === baseCcy || helperModel.nonBaseCcy === baseCcy) {
                let tg = helperModel.traderGroupName + (result.length > 0 ? "/" : "");
                result = tg + result;
              } else {
                let tg = (result.length > 0 ? "/" : "") + helperModel.traderGroupName;
                result = result + tg;
              }
            }
          }
        }
      } else {
        logger.out.error(`${this.constructor.name}.getTraderGroupNames() no allocations`);
      }
    }
    return result === "" ? "-" : result;
  }

  public getTraderGroupIds(params: any): any[] {
    let result: any[] = [];
    let value = params.data ? params.data : params;
    let orderData: CubiqTS.OrderDataImpl = value != null ? value : null;

    if (
      orderData instanceof CubiqTS.OrderDataImpl &&
      orderData.orderType === CubiqTS.OrderType.BENCHMARK
    ) {
      if (
        orderData.allocationData.length >= 1 &&
        orderData.allocationData[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        let allocationData: CubiqTS.AllocationDataImpl = orderData.allocationData[0];
        // Map<allocationId, Map<baseCcy, traderGroup>>
        let allocationMetaMap: Map<string, BmarkAllocationHelperModel> = new Map<
          string,
          BmarkAllocationHelperModel
        >();
        this.processAllocation(allocationData, allocationMetaMap);
        if (
          allocationData.allocationType === CubiqTS.AllocationType.PRIME ||
          allocationMetaMap.size === 1
        ) {
          // CROSS || PRIME
          let traderGroupId =
            allocationMetaMap.get(allocationData.allocationId?.toString()) instanceof
            BmarkAllocationHelperModel
              ? allocationMetaMap.get(allocationData.allocationId?.toString()).traderGroupId
              : null;
          result.push(traderGroupId);
        } else if (allocationMetaMap.size > 1) {
          // component allocations
          for (let [key, helperModel] of allocationMetaMap.entries()) {
            result.push(helperModel.traderGroupId);
          }
        }
      } else {
        logger.out.error(`${this.constructor.name}.getTraderGroupIds() order has no allocations`);
      }
    }
    return result;
  }

  private processAllocation(
    allocationData: CubiqTS.AllocationDataImpl,
    allocationMetaMap: Map<string, BmarkAllocationHelperModel>,
  ): void {
    if (allocationData instanceof CubiqTS.AllocationDataImpl) {
      let baseCcy = typeof allocationData.baseCcy === "string" ? allocationData.baseCcy : null;
      let nonBaseCcy =
        typeof allocationData.nonbaseCcy === "string" ? allocationData.nonbaseCcy : null;
      let traderGroupName = null;
      if (allocationData.deskId instanceof CubiqTS.Long) {
        let traderGroup: CubiqTS.TradingGroupImpl = this.traderGroupLookup.getTraderGroup(
          allocationData.deskId,
        );
        traderGroupName =
          traderGroup instanceof CubiqTS.TradingGroupImpl ? traderGroup.groupCode : null;
      }

      if (
        typeof traderGroupName === "string" &&
        typeof baseCcy === "string" &&
        !allocationMetaMap.has(allocationData.allocationId?.toString())
      ) {
        let helperModel: BmarkAllocationHelperModel = new BmarkAllocationHelperModel(
          allocationData.allocationId,
        );
        helperModel.baseCcy = baseCcy;
        helperModel.nonBaseCcy = nonBaseCcy;
        helperModel.traderGroupName = traderGroupName;
        helperModel.allocationType = allocationData.allocationType;
        helperModel.traderGroupId = allocationData.deskId;
        allocationMetaMap.set(allocationData.allocationId?.toString(), helperModel);
      }

      // component allocations
      if (
        Array.isArray(allocationData.componentAllocations) &&
        allocationData.componentAllocations.length > 0 &&
        allocationData.componentAllocations[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        if (allocationData.componentAllocations.length > 0) {
          for (let allocationItem of allocationData.componentAllocations) {
            this.processAllocation(allocationItem, allocationMetaMap);
          }
        }
      }
    }
  }
}
